import { useAppDispatch, useAppSelector } from "@/store/hooks";
import {
  closeRightClickMenu,
  copyElement,
  deleteElement,
  pasteElement,
  setLayerModalOpen,
  toggleAspectLock,
} from "@/store/slideEditorSlice";
import { Button, IconButton } from "@ai-teach/ui-web";
import React, { useEffect } from "react";
import { FaLock, FaUnlock } from "react-icons/fa";
import {
  MdContentCopy,
  MdContentPaste,
  MdKeyboardArrowRight,
  MdKeyboardCommandKey,
  MdLayers,
  MdOutlineControlPointDuplicate,
  MdOutlineDelete,
  MdOutlineLock,
} from "react-icons/md";
import { TbLetterC, TbLetterD, TbLetterV } from "react-icons/tb";

export interface Action {
  id: string;
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  onClick: (e: React.MouseEvent<HTMLButtonElement>) => void;
  hintIcon: React.ComponentType<{ className?: string }> | string;
  disabled?: boolean;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
}

export function RightClickMenu() {
  const { slide, isAspectLocked, isLayerModalOpen, rightClickMenu } = useAppSelector(
    (state) => state.slideEditor.present
  );
  const dispatch = useAppDispatch();
  const [position, setPosition] = React.useState<{ top: number; left: number } | undefined>(
    undefined
  );
  const menuRef = React.useRef<HTMLDivElement>(null);

  const element = slide?.elements.find((el) => el.id === rightClickMenu.elementId);

  const handleCopy = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    if (!element) return;
    dispatch(copyElement(element));
    dispatch(closeRightClickMenu());
  };

  const handlePaste = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    dispatch(pasteElement());
    dispatch(closeRightClickMenu());
  };

  const handleDuplicate = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    if (!element) return;
    dispatch(copyElement(element));
    dispatch(pasteElement());
    dispatch(closeRightClickMenu());
  };

  const handleDelete = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    if (rightClickMenu.elementId) {
      dispatch(deleteElement(rightClickMenu.elementId));
    }
    dispatch(closeRightClickMenu());
  };

  const handleLayer = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    dispatch(setLayerModalOpen(!isLayerModalOpen));
  };

  const handleLock = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    dispatch(toggleAspectLock());
  };

  const ACTIONS: Action[] = [
    {
      id: "copy",
      icon: MdContentCopy,
      label: "Copy",
      onClick: handleCopy,
      hintIcon: () => (
        <div className="flex items-center gap-1">
          <MdKeyboardCommandKey className="w-3 h-3" />
          <TbLetterC className="w-3 h-3" />
        </div>
      ),
    },
    {
      id: "paste",
      icon: MdContentPaste,
      label: "Paste",
      onClick: handlePaste,
      hintIcon: () => (
        <div className="flex items-center gap-1">
          <MdKeyboardCommandKey className="w-3 h-3" />
          <TbLetterV className="w-3 h-3" />
        </div>
      ),
    },
    {
      id: "duplicate",
      icon: MdOutlineControlPointDuplicate,
      label: "Duplicate",
      onClick: handleDuplicate,
      hintIcon: () => (
        <div className="flex items-center gap-1">
          <MdKeyboardCommandKey className="w-3 h-3" />
          <TbLetterD className="w-3 h-3" />
        </div>
      ),
    },
    {
      id: "delete",
      icon: MdOutlineDelete,
      label: "Delete",
      onClick: handleDelete,
      hintIcon: () => (
        <div className="flex items-center gap-1 px-1 py-[1px] border border-slate-300/60 rounded-md text-[11px] text-slate-600 bg-white/50">
          Delete
        </div>
      ),
    },
    {
      id: "layer",
      icon: MdLayers,
      label: "Layer",
      onClick: handleLayer,
      hintIcon: MdKeyboardArrowRight,
    },
    {
      id: "lock",
      icon: isAspectLocked ? FaUnlock : FaLock,
      label: isAspectLocked ? "Unlock" : "Lock",
      onClick: handleLock,
      hintIcon: () => <div />,
    },
  ];

  // Update position continuously to follow the element
  useEffect(() => {
    if (!rightClickMenu.isOpen || !element) {
      dispatch(closeRightClickMenu());
      setPosition(undefined);
      return;
    }

    if (!rightClickMenu.isOpen || !rightClickMenu.elementId) {
      setPosition(undefined);
      return;
    }

    let animationFrameId: number;

    const updatePosition = () => {
      // Find the element in the DOM by data-element-id attribute
      const domElement = document.querySelector(`[data-element-id="${rightClickMenu.elementId}"]`);

      if (domElement && menuRef) {
        const rect = domElement.getBoundingClientRect();
        const menuRect = menuRef.current?.getBoundingClientRect();
        const menuWidth = menuRect?.width ?? 200;
        const menuHeight = menuRect?.height ?? 326;
        const gap = 10;

        let left = rect.right + gap;
        let top = rect.top + rect.height / 2;

        // Check right boundary - if menu would go off screen, position to left of element
        if (left + menuWidth > window.innerWidth) {
          left = rect.left - menuWidth - gap;
        }

        // Check left boundary - ensure it doesn't go off left edge
        if (left < 0) {
          left = gap;
        }

        // Check bottom boundary
        if (top + menuHeight / 2 > window.innerHeight) {
          top = window.innerHeight - menuHeight / 2 - gap;
        }

        // Check top boundary
        if (top - menuHeight / 2 < 0) {
          top = menuHeight / 2 + gap;
        }

        setPosition({
          top,
          left,
        });
      }

      // Schedule next update
      animationFrameId = requestAnimationFrame(updatePosition);
    };

    // Start the update loop
    animationFrameId = requestAnimationFrame(updatePosition);

    // Cleanup: cancel animation frame on unmount
    return () => {
      cancelAnimationFrame(animationFrameId);
    };
  }, [rightClickMenu.isOpen, rightClickMenu.elementId, dispatch, element]);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = () => {
      if (rightClickMenu.isOpen) {
        dispatch(closeRightClickMenu());
        dispatch(setLayerModalOpen(false));
      }
    };

    const handleContextMenu = (e: MouseEvent) => {
      e.preventDefault();
    };

    if (rightClickMenu.isOpen) {
      document.addEventListener("click", handleClickOutside);
      document.addEventListener("contextmenu", handleContextMenu);
    }

    return () => {
      document.removeEventListener("click", handleClickOutside);
      document.removeEventListener("contextmenu", handleContextMenu);
    };
  }, [rightClickMenu.isOpen, dispatch]);

  if (!rightClickMenu.isOpen || !position) return null;

  return (
    <div
      data-right-click-menu
      ref={menuRef}
      className="fixed z-[900]"
      style={{
        left: `${position.left}px`,
        top: `${position.top}px`,
        transform: "translateY(-50%)", // Center vertically
      }}
    >
      <div className="bg-white/90 backdrop-blur-xl rounded-2xl shadow-2xl border-2 border-slate-200/80 p-3 w-[200px]">
        {/* First 4 actions */}
        {ACTIONS.slice(0, 4).map((action) => (
          <ActionButton key={action.id} {...action} />
        ))}

        <div className="h-px bg-gradient-to-r from-transparent via-slate-200 to-transparent my-2" />

        {/* Last 2 actions */}
        {ACTIONS.slice(4).map((action) => (
          <ActionButton key={action.id} {...action} />
        ))}
      </div>
    </div>
  );
}

function ActionButton({
  icon: Icon,
  label,
  onClick,
  disabled,
  hintIcon,
  onMouseEnter,
  onMouseLeave,
}: Action) {
  const isDelete = label === "Delete";

  return (
    <Button
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      disabled={disabled}
      variant="ghost"
      className={`group flex items-center gap-3 px-2 py-2 mb-2 w-full justify-between
        hover:${isDelete ? "bg-red-100" : "bg-primary-50"}
        rounded-xl transition-all duration-200 border-2 border-transparent
        ${isDelete ? "hover:border-red-300" : "hover:border-primary-300"}
        shadow-sm hover:shadow-md hover:-translate-y-0.5
        active:translate-y-0 active:scale-95`}
    >
      <div className="flex gap-4 items-center">
        <Icon
          className={`w-5 h-5 text-slate-600 ${isDelete ? "group-hover:text-red-600" : "group-hover:text-primary-600"}`}
        />
        <span
          className={`text-xs font-medium text-slate-700 ${isDelete ? "group-hover:text-red-900" : "group-hover:text-slate-900"}`}
        >
          {label}
        </span>
      </div>
      <div>{typeof hintIcon === "function" ? React.createElement(hintIcon) : hintIcon}</div>
    </Button>
  );
}
